import type { LampDesign } from 'lamp-core/src/types/lampDesign';

const API_BASE_URL = import.meta.env.VITE_API_URL;

export async function saveLampDesign(design: Omit<LampDesign, 'id' | 'createdAt' | 'updatedAt'> & Partial<Pick<LampDesign, 'id' | 'createdAt' | 'updatedAt'>>, cacheId?: string) {
  // Fill in missing fields (createdAt, updatedAt) if not provided
  const payload: any = {
    ...design,
    createdAt: design.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else if (cacheId) {
    // For guest users, include cacheId in the payload
    payload.cacheId = cacheId;
  }

  const res = await fetch(`${API_BASE_URL}/api/lamp-designs`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to save lamp design');
  return await res.json();
}

export async function getLampDesigns() {
  const res = await fetch(`${API_BASE_URL}/api/lamp-designs`);
  if (!res.ok) throw new Error('Failed to fetch lamp designs');
  return await res.json();
}

export async function getLampDesign(id: string, cacheId?: string) {
  const token = localStorage.getItem('token');
  const url = new URL(`${API_BASE_URL}/api/lamp-designs/${id}`);
  
  // If guest user (no token), add cacheId as query parameter
  if (!token && cacheId) {
    url.searchParams.set('cacheId', cacheId);
  }
  
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error('Unauthorized: You do not have access to this design');
    }
    if (res.status === 404) {
      throw new Error('Design not found');
    }
    throw new Error('Failed to fetch lamp design');
  }
  return await res.json();
}

export async function deleteLampDesign(id: string, cacheId?: string) {
  const token = localStorage.getItem('token');
  const url = new URL(`${API_BASE_URL}/api/lamp-designs/${id}`);
  
  // If guest user (no token), add cacheId as query parameter
  if (!token && cacheId) {
    url.searchParams.set('cacheId', cacheId);
  }
  
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error('Unauthorized: You do not have permission to delete this design');
    }
    if (res.status === 401) {
      throw new Error('Authentication required or cacheId must be provided');
    }
    throw new Error('Failed to delete lamp design');
  }
  // Don't try to parse JSON for 204 No Content response
  if (res.status !== 204) {
    return await res.json();
  }
  return null;
} 